import { normalizeScanInput } from '@/kiosk-core/scanner/scanInputPipeline';
import { isKnownTicketFormat } from './ticketFormat';

export interface ParsedScanCode {
  code: string;
  source: 'raw' | 'url-query' | 'url-path';
}

const MAX_QR_CODE_LENGTH = 64;
const URL_QUERY_KEYS = [
  'qr_code',
  'qr',
  'code',
  'ticket',
  'ticket_code',
  'reservation_id',
  'id',
] as const;

export function parseEntrScanCode(input: string): ParsedScanCode | null {
  const normalized = normalizeScanInput(input);
  if (!normalized) return null;

  if (isHttpUrl(normalized)) {
    return parseHttpUrlScanCode(normalized);
  }

  return acceptQrCode(normalized, 'raw');
}

function parseHttpUrlScanCode(input: string): ParsedScanCode | null {
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return null;
  }

  for (const key of URL_QUERY_KEYS) {
    const value = url.searchParams.get(key);
    const accepted = acceptQrCode(value ?? '', 'url-query');
    if (accepted) return accepted;
  }

  const segments = url.pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  if (!lastSegment) return null;

  let decoded = lastSegment;
  try {
    decoded = decodeURIComponent(lastSegment);
  } catch {
    decoded = lastSegment;
  }

  return acceptQrCode(decoded, 'url-path');
}

function acceptQrCode(
  input: string,
  source: ParsedScanCode['source']
): ParsedScanCode | null {
  // Canonicalize to upper-case here, the way the greenscreen kiosks do upstream
  // in their `sanitize` step. The Fever/venue allow-list pattern is upper-case
  // only AND the backend ticket lookup is case-sensitive on upper-case data, so
  // a lower/mixed-case scan must be folded once, at the boundary, or a genuine
  // ticket would be rejected here (or silently miss at the backend). Done after
  // URL extraction so query-param keys aren't case-mangled.
  const code = normalizeScanInput(input).toUpperCase();
  if (!code || code.length > MAX_QR_CODE_LENGTH) return null;
  // Rigorous allow-list: only real ticket formats (Fever 20-char, employee,
  // venue barcodes) pass. A wrong scan — exhibit-label QR, product barcode,
  // waiver/marketing URL, truncated barcode — is rejected here so it never
  // becomes a junk backend lookup and a "ticket not found" popup. Applies to
  // every extraction path (raw, URL query param, URL path segment).
  if (!isKnownTicketFormat(code)) return null;
  return { code, source };
}

function isHttpUrl(input: string): boolean {
  return /^https?:\/\//i.test(input);
}
